/**
 * File: auditHistoryUtils.ts
 * Path: apps/admin/src/shared/utils/auditHistoryUtils.ts
 * Description: Every audit of a lead's website, newest first: the runs saved
 * from the lead page (reprinted on demand) and the reports attached from
 * audits run elsewhere (downloaded as attached).
 */

export interface AuditRun {
  auditId: string;
  domain: string;
  /** Null for runs saved before each run kept its own score. */
  score: number | null;
  at: number | null;
}

export interface AuditReport {
  path: string;
  name: string;
  url: string;
  at: number;
}

export type AuditHistoryEntry =
  | ({ kind: "run" } & AuditRun)
  | ({ kind: "report" } & AuditReport);

const millis = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value && typeof value === "object" && typeof (value as { toMillis?: unknown }).toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

/** One saved run, read from leads/<id>/audits/<auditId>. */
export const toAuditRun = (id: string, data: Record<string, unknown>): AuditRun => ({
  auditId: typeof data.auditId === "string" && data.auditId ? data.auditId : id,
  domain: typeof data.domain === "string" ? data.domain : "",
  score:
    typeof data.score === "number" && Number.isFinite(data.score)
      ? Math.max(0, Math.min(100, Math.round(data.score)))
      : null,
  at: millis(data.createdAt) ?? millis(data.generatedAt),
});

/**
 * Runs and attached reports in one list, newest first. The latest run's
 * score comes from the lead itself when the run didn't keep its own.
 */
export const auditHistory = (
  runs: readonly AuditRun[],
  reports: readonly AuditReport[],
  latest: { auditId: string; score: number } | null,
): AuditHistoryEntry[] =>
  [
    ...runs.map((run) => ({
      kind: "run" as const,
      ...run,
      score: run.score ?? (latest && latest.auditId === run.auditId ? latest.score : null),
    })),
    ...reports.map((report) => ({ kind: "report" as const, ...report })),
  ].sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
